import { useMemo } from 'react';
import {
  qAppConfig,
  getMainModuleConfig,
  isClinicModuleEnabled,
  type AppConfigRow,
  type ClinicMainModuleConfig,
  type ClinicMainModuleKey,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../adapters/powersync';

/**
 * PRD.283 — reactive read of the clinic's main-module configuration from the
 * singleton app_config row (extra_json.main_modules). This is the single place
 * Clinic Settings, Consultation and Queue read the value from, so raw JSON is
 * never parsed independently.
 *
 * The setting is RESTRICTIVE ONLY: a missing value means enabled, so existing
 * clinics keep every module without a migration. Platform feature flags and
 * user permissions remain authoritative above this preference.
 */
export function useClinicMainModules(): {
  config: ClinicMainModuleConfig;
  isModuleEnabled: (module: ClinicMainModuleKey) => boolean;
} {
  const { data } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  return useMemo(() => {
    const config = getMainModuleConfig(data?.[0] ?? null);
    return {
      config,
      isModuleEnabled: (module: ClinicMainModuleKey) => isClinicModuleEnabled(config, module),
    };
  }, [data]);
}

/** Convenience: reactive boolean for the Invoice module (defaults to enabled). */
export function useInvoiceModuleEnabled(): boolean {
  const { isModuleEnabled } = useClinicMainModules();
  return isModuleEnabled('invoice');
}

/**
 * PRD.305 — reactive boolean for the Laboratory module (defaults to enabled).
 *
 * Disabling it hides the dedicated Laboratory navigation, route and every
 * operational action. It does NOT hide clinical history: Patient Workspace and
 * Consultation fall back to published-results-only rather than disappearing —
 * that fallback lives in `resolveLaboratoryCapabilities`, not here.
 */
export function useLaboratoryModuleEnabled(): boolean {
  const { isModuleEnabled } = useClinicMainModules();
  return isModuleEnabled('laboratory');
}
